/**
 * EPUB Search Highlight Service
 *
 * Manages temporary search result highlights in EPUB documents using epubjs annotations API.
 * Unlike permanent highlights (which are stored in the backend), search highlights are ephemeral
 * and disappear when the search is closed.
 *
 * All search-highlight CFIs are tracked in a module-level Set so that:
 *  1. restoreHighlights (user annotations) can skip them deterministically,
 *  2. clearing doesn't depend on stale search result arrays or old rendition references.
 */

/**
 * Module-level set of CFI strings that are currently registered as search
 * highlights.  Exported so the EpubViewer's restoreHighlights effect can
 * distinguish user annotations from search annotations.
 */
export const activeSearchHighlightCfis = new Set();

/**
 * Check if a given annotation is a search-highlight annotation.
 * Works by inspecting the annotation's `data.isSearchResult` flag and/or
 * its className, plus the authoritative `activeSearchHighlightCfis` set.
 *
 * @param {Object} annotation - epubjs Annotation instance from _annotations
 * @returns {boolean}
 */
export const isSearchHighlightAnnotation = (annotation) => {
  if (!annotation) return false;
  // Primary: check the tracked set (most reliable after re-apply cycles)
  if (annotation.cfiRange && activeSearchHighlightCfis.has(annotation.cfiRange))
    return true;
  // Secondary: check data flag set during add()
  if (annotation.data?.isSearchResult) return true;
  // Tertiary: check CSS class
  if (annotation.className === "epub-search-match") return true;
  return false;
};

/**
 * Apply search highlights to visible EPUB content.
 * Idempotent — already-highlighted CFIs are skipped.
 *
 * @param {Object} rendition - epubjs Rendition instance
 * @param {Array} searchResults - Search results from searchService.searchEpub()
 * @param {number} currentChunkIndex - Current chapter/chunk being displayed
 */
export const applyEpubSearchHighlights = (
  rendition,
  searchResults,
  currentChunkIndex,
) => {
  if (
    !rendition ||
    !rendition.annotations ||
    !Array.isArray(searchResults) ||
    searchResults.length === 0
  ) {
    return;
  }

  try {
    const chunkNum = Number(currentChunkIndex);

    // Filter search results for current chapter
    // Use chapter_number for chunked mode, sectionIndex for non-chunked
    const relevantResults = searchResults.filter((result) => {
      const resultChapter = result.chapter_number ?? result.sectionIndex;
      return Number(resultChapter) === chunkNum;
    });

    if (relevantResults.length === 0) {
      return;
    }

    const views = rendition.views();
    if (!views?._views?.length) {
      return;
    }

    relevantResults.forEach((result) => {
      const cfiRange = result.cfi;
      if (!cfiRange) return;

      // Skip if already tracked (idempotent)
      if (activeSearchHighlightCfis.has(cfiRange)) return;

      try {
        rendition.annotations.add(
          "highlight",
          cfiRange,
          { isSearchResult: true, query: result.query },
          null, // no click callback for search results
          "epub-search-match",
        );
        activeSearchHighlightCfis.add(cfiRange);
      } catch {
        // Continue with next result on error
      }
    });
  } catch (error) {
    console.error("Error in applyEpubSearchHighlights:", error);
  }
};

/**
 * Remove ALL search highlights from the rendition.
 * Uses the authoritative `activeSearchHighlightCfis` set so it works
 * correctly even after a chapter switch (no stale result array needed).
 *
 * @param {Object} rendition - epubjs Rendition instance
 */
export const clearAllEpubSearchHighlights = (rendition) => {
  if (!rendition?.annotations || activeSearchHighlightCfis.size === 0) {
    return;
  }

  try {
    // Snapshot the set — we'll mutate it as we iterate
    const cfis = [...activeSearchHighlightCfis];
    for (const cfi of cfis) {
      try {
        rendition.annotations.remove(cfi, "highlight");
      } catch {
        // Silently ignore errors (CFI might not exist in current rendition)
      }
    }
  } catch (error) {
    console.error("Error in clearAllEpubSearchHighlights:", error);
  } finally {
    activeSearchHighlightCfis.clear();
  }
};

/**
 * Reset the tracking set without touching the rendition.
 * Call this when the rendition is being destroyed (CFIs become invalid anyway).
 */
export const resetSearchHighlightTracking = () => {
  activeSearchHighlightCfis.clear();
};
